class Wishlist {
  constructor() {
    this.storageKey = 'noopy_wishlist';
    this.customerId = window.__wishlistCustomerId || null;
    this.cloudSynced = false;

    /* Initialiser en local d'abord, puis sync Firebase quand prêt */
    this.init();

    if (this.customerId) {
      /* Client connecté : attendre Firebase puis synchroniser */
      if (window.__wishlistFirestore) {
        this._syncWithCloud();
      } else {
        window.addEventListener('firebase:ready', () => this._syncWithCloud(), { once: true });
      }
    }
  }

  /* ─────────────────────────────────────────
     LOCALSTORAGE (toujours utilisé en local)
  ───────────────────────────────────────── */

  getWishlist() {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : [];
  }

  saveWishlist(list) {
    localStorage.setItem(this.storageKey, JSON.stringify(list));
    this.updateHeaderCount();
    /* Sync cloud si client connecté */
    if (this.customerId && this.cloudSynced) {
      this._saveToCloud(list);
    }
  }

  /* ─────────────────────────────────────────
     FIREBASE (cloud)
  ───────────────────────────────────────── */

  async _syncWithCloud() {
    try {
      const { db, doc, getDoc, setDoc } = window.__wishlistFirestore;
      const ref = doc(db, 'wishlists', this.customerId);
      const snap = await getDoc(ref);

      const localList  = this.getWishlist();
      const cloudList  = snap.exists() ? (snap.data().items || []) : [];

      /* Fusion : local + cloud sans doublons (clé = id produit) */
      const merged = this._mergeLists(localList, cloudList);

      /* Sauvegarder la liste fusionnée partout */
      localStorage.setItem(this.storageKey, JSON.stringify(merged));
      await setDoc(ref, { items: merged, updatedAt: new Date().toISOString() });

      this.cloudSynced = true;
      this.init(); /* Rafraîchir les cœurs avec la liste fusionnée */

    } catch (err) {
      console.error('[Wishlist] Erreur sync Firebase:', err);
      this.cloudSynced = false;
    }
  }

  async _saveToCloud(list) {
    try {
      const { db, doc, setDoc } = window.__wishlistFirestore;
      const ref = doc(db, 'wishlists', this.customerId);
      await setDoc(ref, { items: list, updatedAt: new Date().toISOString() });
    } catch (err) {
      console.error('[Wishlist] Erreur sauvegarde Firebase:', err);
    }
  }

  /* Fusion de deux listes sans doublons, les plus récents en premier */
  _mergeLists(listA, listB) {
    const map = new Map();
    /* listB en premier (cloud) puis listA (local) écrase si même ID */
    [...listB, ...listA].forEach(item => {
      if (item && item.id) map.set(String(item.id), item);
    });
    return Array.from(map.values());
  }

  /* ─────────────────────────────────────────
     OPÉRATIONS PRODUIT
  ───────────────────────────────────────── */

  addProduct(productId, productTitle, productImage, productUrl, productPrice) {
    const list = this.getWishlist();
    if (!this.isInWishlist(productId)) {
      let fixedImage = productImage;
      if (fixedImage && fixedImage.startsWith('//')) {
        fixedImage = 'https:' + fixedImage;
      }
      list.push({
        id: String(productId),
        title: productTitle,
        image: fixedImage,
        url: productUrl,
        price: productPrice,
        addedAt: new Date().toISOString()
      });
      this.saveWishlist(list);
      this.updateAllHearts();
      return true;
    }
    return false;
  }

  removeProduct(productId) {
    const list = this.getWishlist().filter(item => String(item.id) !== String(productId));
    this.saveWishlist(list);
    this.updateAllHearts();
  }

  isInWishlist(productId) {
    return this.getWishlist().some(item => String(item.id) === String(productId));
  }

  toggleProduct(productId, productTitle, productImage, productUrl, productPrice) {
    if (this.isInWishlist(productId)) {
      this.removeProduct(productId);
      return false;
    } else {
      this.addProduct(productId, productTitle, productImage, productUrl, productPrice);
      return true;
    }
  }

  getCount() {
    return this.getWishlist().length;
  }

  /* ─────────────────────────────────────────
     UI
  ───────────────────────────────────────── */

  init() {
    this.updateAllHearts();
    this.updateHeaderCount();
  }

  updateAllHearts() {
    document.querySelectorAll('[data-wishlist-button]').forEach(button => {
      const productId = button.dataset.productId;
      const productTitle = button.dataset.productTitle;
      if (this.isInWishlist(productId)) {
        button.classList.add('active');
        button.setAttribute('aria-label', `Retirer ${productTitle} de la liste de souhaits`);
        button.setAttribute('title', 'Retirer de la liste de souhaits');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-label', `Ajouter ${productTitle} à la liste de souhaits`);
        button.setAttribute('title', 'Ajouter à la liste de souhaits');
      }
    });
  }

  updateHeaderCount() {
    const count = this.getWishlist().length;
    const counter = document.querySelector('[data-wishlist-count]');
    const counterMobile = document.querySelector('[data-wishlist-count-mobile]');
    const headerIcon = document.querySelector('.header__icon--wishlist');

    if (counter) {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
    }
    if (counterMobile) {
      counterMobile.textContent = count;
      counterMobile.style.display = count > 0 ? 'flex' : 'none';
    }
    if (headerIcon) {
      headerIcon.classList.toggle('has-items', count > 0);
    }
  }
}

/* ─────────────────────────────────────────
   INITIALISATION
───────────────────────────────────────── */
const wishlist = new Wishlist();

/* Gérer les clics sur les boutons wishlist */
document.addEventListener('click', (e) => {
  const button = e.target.closest('[data-wishlist-button]');
  if (!button) return;
  e.preventDefault();

  wishlist.toggleProduct(
    button.dataset.productId,
    button.dataset.productTitle,
    button.dataset.productImage,
    button.dataset.productUrl,
    button.dataset.productPrice
  );
});